import { parseItems } from './items.js'
import { describeOrder } from './service.js'
import { budgetFor } from './budget.js'
import { STEP } from './steps.js'
import { toE164 } from './phone.js'

/**
 * The wizard's own state, and the body it becomes.
 *
 * Beside `constants.js` rather than inside it, so the shipped two-step form keeps working
 * while this is built. Both die together at B7 — this file takes over, and
 * `makeInitialState` / `toOrderBody` / `ItemRows` go with the old screens.
 *
 * **The payload shape does not change.** Not one key is added, removed or renamed. That
 * was the binding constraint on the whole redesign: the rider's app, the partner console
 * and the admin console all read this body, and none of them is being touched.
 */

export function makeEmptyLocation() {
  return { query: '', lat: null, lng: null, label: '' }
}

/**
 * A remembered place, in the shape the location state speaks.
 *
 * `query` is set to the label rather than left blank, because that is what choosing a
 * suggestion does (`useLocationSearch.selectSuggestion`) and the box has to read the same
 * whether the answer came from a tap today or a tap last week.
 *
 * A remembered place with no label — the *"gamitin ang lokasyon ko ngayon"* answer keeps
 * none, on purpose, rather than inventing a street name for a pin — seeds an empty box over
 * a placed pin. That is not a broken state: it is exactly what the screen already shows
 * after that button is pressed, down to the caption falling back to the coordinates.
 */
function locationFrom(place) {
  if (!Number.isFinite(place?.lat) || !Number.isFinite(place?.lng)) return makeEmptyLocation()

  const label = String(place.label ?? '')

  return { query: label, lat: place.lat, lng: place.lng, label }
}

/**
 * One screen's worth of answer per field, and nothing the account already knows.
 *
 * No name and no email — those come from the signed-in Google account, and asking a
 * customer to type their own name to receive their own order is a screen that exists for
 * the database rather than for them.
 *
 * **`remembered` is the field the account could not cover.** Google hands over a name and an
 * email and never a mobile number, so `phoneNumber` was the one thing a returning customer
 * still typed out on every single order — see `places.js`. It arrives as an argument rather
 * than being read here, so this stays a pure function of its inputs and the tests below it
 * keep describing a blank form when they ask for one.
 *
 * @param {string} partnerSlug
 * @param {{phoneNumber?: string, dropoffContactName?: string}} [remembered]
 */
export function makeOrderState(partnerSlug = '', remembered = {}) {
  return {
    step: STEP.SERVICE,
    submitting: false,
    partnerSlug,

    /** Step 1. Never sent as a field — it writes `itemDetails.description`. */
    service: 'pabili',

    /**
     * Step 2. `pickupSource` is the shop name, and only the shop name.
     *
     * **Seeded only from a link, never from memory.** The shop changes from order to order,
     * so a pickup filled in from the last one would be wrong more often than right — the
     * remembered shops are offered as suggestions on the step instead, where choosing is
     * still the customer's move.
     *
     * A link is different: somebody arriving from the public price checker named this shop
     * seconds ago and has already been quoted on it. Opening the form on anything else
     * would change the delivery they just priced.
     */
    pickup: locationFrom(remembered.pickup),
    pickupSource: String(remembered.pickupSource ?? ''),

    /**
     * Step 3, skippable.
     *
     * Blank on most shop runs, because nobody is waiting at Jollibee for somebody else's
     * order — the rider buys it. Blank means the rider gets the customer's own number,
     * which is the right answer when a problem at the counter needs a decision.
     */
    pickupContactName: '',
    pickupContactPhone: '',

    /** Step 4. One textarea; `parseItems` turns it into `itemDetails.items[]`. */
    itemsText: '',

    /** Step 5, pabili only. `maxBudget` is derived at +30% and stays editable. */
    estimatedTotal: '',
    maxBudget: '',

    /**
     * Step 6, and the one seed that removes the map.
     *
     * `phoneNumber` is this end's number and is what the rider rings on arrival. A blank
     * name means the account holder — the customer never types their own.
     *
     * A house does not move. The dropoff is the hardest question the form asks — search,
     * or drag a pin on a Leaflet map on a cheap Android — and for a returning customer it
     * has the same answer every time. Seeded, the step opens already answered and *Sunod*
     * is the only thing left to press.
     *
     * Only the dropoff. The pickup shop changes from order to order, so a seeded pickup
     * would be wrong more often than right and would have to be cleared before it could be
     * answered — see `PlacePicker`, where the remembered shops are offered as suggestions
     * instead.
     */
    dropoff: locationFrom(remembered.dropoff),
    dropoffContactName: String(remembered.dropoffContactName ?? ''),
    phoneNumber: String(remembered.phoneNumber ?? ''),

    /** Step 7, skippable. */
    pickupTime: '',
    deliveryTime: '',

    /**
     * Not a question the customer answers, and not a field on any screen.
     *
     * The rider the public price checker measured this delivery from, signed by the server
     * — see `backend/src/utils/riderHint.js`. It rides through the form untouched and is
     * handed back at placement so that rider gets the first offer.
     *
     * Empty for every customer who did not arrive from the price checker, which is most of
     * them, and the server treats an absent one exactly as it treats a rejected one.
     */
    riderHint: String(remembered.riderHint ?? ''),

    /**
     * Step 8. `deliveryFeePaid` is a second question, not a third payment method:
     * `paymentMethod` says how the *goods* settled, this says whether the *ride* did, and
     * they vary independently. Left wrong, a rider asks for money already in the
     * partner's account.
     */
    paymentMethod: 'cash_on_delivery',
    deliveryFeePaid: false,
    specialInstructions: '',
  }
}

const trimmedOrNull = (value) => {
  const text = String(value ?? '').trim()
  return text || null
}

/**
 * A pin written out, for a place the customer never named.
 *
 * Five decimals, which is about a metre — enough to be exact and short enough to read
 * aloud. `helpers.coords` is the same idea for the same numbers, and is not reused here
 * only because this file is pure and tested without a DOM.
 */
const coordsAsText = (location) => {
  const lat = Number(location?.lat)
  const lng = Number(location?.lng)

  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null

  return `${lat.toFixed(5)}, ${lng.toFixed(5)}`
}

/** Form state into the body `POST /api/orders` expects. */
export function toOrderBody(state) {
  const { estimatedTotal, maxBudget } = budgetFor(state)

  return {
    partnerSlug: state.partnerSlug,
    phoneNumber: toE164(state.phoneNumber),

    // Sent only when there is one. `payload.js` does not add keys to this body lightly —
    // the rider app, the partner console and the admin console all read it — and an empty
    // string would be a new field on every order for the sake of the few that carry one.
    ...(state.riderHint ? { riderHint: state.riderHint } : {}),

    pickupLatitude: state.pickup.lat,
    pickupLongitude: state.pickup.lng,
    pickupSource: String(state.pickupSource ?? '').trim(),

    dropoffLatitude: state.dropoff.lat,
    dropoffLongitude: state.dropoff.lng,
    /**
     * The address in words, which the app has always had and once discarded.
     *
     * `label` is what the place search returned, or the coordinates as text when the
     * customer pasted a map link. Either is sent as-is: coordinates are no worse than the
     * nothing that used to be there, and the pin stays authoritative regardless. Without
     * it the partner's list shows two decimals and the rider gets a pin and no street.
     */
    /**
     * What the customer called it, and the pin as words when they called it nothing.
     *
     * The fallback is load-bearing now that a hand-dropped pin arrives with an empty label
     * rather than the coordinates as its name — see `MapSheet.pick`. Without it the
     * partner's list and the rider's screen would show nothing at all for the one kind of
     * delivery that most needs describing.
     *
     * Coordinates are a poor address and a real one. The pin stays authoritative either
     * way; this is only what gets read.
     */
    dropoffAddress: trimmedOrNull(state.dropoff.label) ?? coordsAsText(state.dropoff),

    itemDetails: {
      /**
       * Written by `describeOrder`, not by the customer.
       *
       * This was a free-text box asking what the rider is fetching, sitting directly
       * above an item list asking the same thing. The API still requires the field, so
       * the service answer fills it and the box is gone.
       */
      description: describeOrder(state),
      items: parseItems(state.itemsText),
    },

    pickupTime: state.pickupTime ? new Date(state.pickupTime).toISOString() : null,
    deliveryTime: state.deliveryTime ? new Date(state.deliveryTime).toISOString() : null,

    estimatedTotal,
    maxBudget,

    paymentMethod: state.paymentMethod,
    /**
     * Forced false on a cash order, exactly as the server does.
     *
     * The customer can pick "paid online", tick that the fee is included, then go back
     * and switch to cash — and the tick is still sitting in state. Sending it would tell
     * the rider to collect nothing on an order where nothing has been paid.
     */
    deliveryFeePaid: state.paymentMethod === 'cash_on_delivery' ? false : Boolean(state.deliveryFeePaid),

    /**
     * Who the rider rings at each end.
     *
     * The pickup contact is **omitted rather than sent empty**, because the server reads
     * an absent contact as *"the account holder"* — the same person a blank field means.
     * Sending `{ name: null, phoneNumber: null }` would be an empty contact instead of no
     * contact, and those are different things.
     *
     * The drop-off always goes: `phoneNumber` is required and is that end's number.
     */
    dropoffContact: {
      name: trimmedOrNull(state.dropoffContactName),
      phoneNumber: toE164(state.phoneNumber),
    },
    ...(toE164(state.pickupContactPhone)
      ? {
        pickupContact: {
          name: trimmedOrNull(state.pickupContactName),
          phoneNumber: toE164(state.pickupContactPhone),
        },
      }
      : {}),

    /**
     * Only what the customer wrote.
     *
     * An earlier design packed the drop-off name and the delivery-fee flag in here as
     * delimited lines, on the belief that neither had a field. Both do — `dropoffContact`
     * and `deliveryFeePaid` — so nothing is prepended and the customer keeps the whole
     * 500 characters.
     */
    specialInstructions: trimmedOrNull(state.specialInstructions),
  }
}
